import { ChangeEvent, FC, useEffect, useState } from "react";

import Button from "components/Button";
import CodeInput from "components/CodeInput";
import PhoneNumber from "components/PhoneNumber";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  consumeWhatsappRateLimit,
  resetWhatsappVerification,
  sendWhatsappCodeRequest,
  verifyWhatsappCodeRequest,
} from "store/slices/notificationSettingsSlice";

import styles from "../style.module.scss";

type PropTypes = {
  onClose?: () => void;
  userId?: number;
  prefillPhone?: string;
};

const RESEND_SECONDS = 60;
const CODE_LENGTH = 4;

const WhatsAppVerificationContent: FC<PropTypes> = ({ onClose, userId, prefillPhone }) => {
  const dispatch = useAppDispatch();
  const { codeSending, codeSent, codeVerifying, codeError, rateLimited, rateLimitRetryAfter } = useAppSelector(
    (store: RootStore) => store.notificationSettings,
  );

  const [code, setCode] = useState("");
  const [phone, setPhone] = useState(prefillPhone ?? "");
  const [isPhoneValid, setPhoneValid] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(RESEND_SECONDS);

  useEffect(() => {
    dispatch(resetWhatsappVerification());
    return () => void dispatch(resetWhatsappVerification());
  }, [dispatch]);

  useEffect(() => {
    if (codeSent) {
      setTimeLeft(RESEND_SECONDS);
      setFormError(null);
    }
  }, [codeSent]);

  useEffect(() => {
    if (!codeSent || rateLimited || timeLeft <= 0) return;
    const timer = setTimeout(() => setTimeLeft((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [codeSent, rateLimited, timeLeft]);

  useEffect(() => {
    if (!rateLimited || !rateLimitRetryAfter) return;
    const timer = setTimeout(() => {
      dispatch(consumeWhatsappRateLimit());
      setTimeLeft(RESEND_SECONDS);
    }, rateLimitRetryAfter * 1000);
    return () => clearTimeout(timer);
  }, [rateLimited, rateLimitRetryAfter, dispatch]);

  const handleSendCode = () => {
    if (!userId || codeSending) return;
    if (!isPhoneValid) {
      setFormError("Enter a valid phone number.");
      return;
    }
    setFormError(null);
    dispatch(sendWhatsappCodeRequest({ userId, phone }));
  };

  const handleVerify = () => {
    if (!userId || codeVerifying) return;
    if (code.length < CODE_LENGTH) {
      setFormError("Please, enter the 4-digit code.");
      return;
    }
    setFormError(null);
    dispatch(verifyWhatsappCodeRequest({ userId, phone, code }));
  };

  const handleResendCode = () => {
    if (!userId) return;
    setTimeLeft(RESEND_SECONDS);
    dispatch(sendWhatsappCodeRequest({ userId, phone }));
  };

  const handleChangeNumber = () => {
    setCode("");
    setFormError(null);
    dispatch(resetWhatsappVerification());
  };

  const handlePhoneChange = (value: string, valid?: boolean) => {
    setPhone(value);
    setPhoneValid(!!valid);
    setFormError(null);
    if (codeError || rateLimited) dispatch(resetWhatsappVerification());
  };

  const handleCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCode(event.target.value);
    setFormError(null);
  };

  return (
    <div className={styles.content}>
      <p className={styles.description}>
        {!codeSent
          ? "Enter your phone number to receive WhatsApp notifications. We'll send a 4-digit verification code via WhatsApp."
          : `A 4-digit code was sent to ${phone} via WhatsApp. Enter the code below to verify your number.`}
      </p>

      {!codeSent && <PhoneNumber className={styles.phoneNumber} isRequired value={phone} onChange={handlePhoneChange} />}

      {!codeSent && (rateLimited || !!formError || !!codeError) && (
        <p className={styles.error}>
          {rateLimited
            ? "Too many verification attempts for this number. Please try again in 1 hour or use a different number."
            : (formError ?? codeError)}
        </p>
      )}

      {codeSent && (
        <div className={styles.codeInputWrapper}>
          <CodeInput value={code} onChange={handleCodeChange} error={(formError ?? codeError) ?? undefined} />

          {rateLimited ? (
            <p className={styles.codeDescription}>
              Resend limit reached. Please try again in 1 hour or change the number.
            </p>
          ) : (
            <p className={styles.codeDescription}>
              Didn’t get the code?{" "}
              <Button
                variant="link"
                onClick={handleResendCode}
                className={styles.resendCodeButton}
                disabled={timeLeft > 0 || codeSending}
              >
                Resend {timeLeft > 0 ? `(${timeLeft}s)` : ""}
              </Button>
            </p>
          )}

          <Button variant="link" onClick={handleChangeNumber} className={styles.changeNumber}>
            Change number
          </Button>
        </div>
      )}

      <div className={styles.buttons}>
        <Button variant="secondary" onClick={onClose} className={styles.cancelButton}>
          Cancel
        </Button>

        {!codeSent ? (
          <Button variant="primary" onClick={handleSendCode} className={styles.verifyButton}>
            Send code
          </Button>
        ) : (
          <Button variant="primary" onClick={handleVerify} className={styles.verifyButton}>
            Verify
          </Button>
        )}
      </div>
    </div>
  );
};

export default WhatsAppVerificationContent;
